/**
 * `<playing-card>`: one card, drawn face up, face down, or as the cap on a
 * finished book.
 *
 * A card is always decoration. Whatever it sits in - a button in the hand, a
 * pile in the well, a meld on the felt - carries the accessible name, because
 * that is where the meaning is: "discard the seven", not just "seven".
 */

import { type Card, type Rank, isWild } from "@ryzun/haf-saloon-game-engine";
import { getRankGlyph, getSuitGlyph } from "../cards.js";
import { h, setAttributes } from "../dom.js";
import { BaseElement } from "./base-element.js";
import styles from "./playing-card.css";

/** The solid card on top of a finished book, which shows the rank alone. */
export interface BookCap {
    readonly rank: Rank;
    readonly pure: boolean;
}

export class PlayingCard extends BaseElement {
    #card: Card | null = null;

    #cap: BookCap | null = null;

    constructor() {
        super(styles);
    }

    /** The face to show, or null for a card back. */
    get card(): Card | null {
        return this.#card;
    }

    set card(value: Card | null) {
        if (value !== this.#card) {
            this.#card = value;
            this.invalidate();
        }
    }

    get cap(): BookCap | null {
        return this.#cap;
    }

    set cap(value: BookCap | null) {
        this.#cap = value;
        this.invalidate();
    }

    protected render(): void {
        const card = this.#card;
        const cap = this.#cap;

        setAttributes(this, {
            "aria-hidden": "true",
            "back": card === null && cap === null,
            "cap": cap === null ? null : cap.pure ? "pure" : "wild",
            "data-color": cap === null ? card?.color : null,
            "data-rank": cap?.rank ?? card?.rank,
            "data-suit": cap === null ? card?.suit : null,
            "wild": card !== null && isWild(card)
        });

        this.root.replaceChildren(h("div", { class: "face", part: "face" }, ...this.#renderFace()));
    }

    #renderFace(): readonly (Node | string)[] {
        if (this.#cap !== null) {
            return [h("span", { class: "cap-rank" }, getRankGlyph(this.#cap.rank))];
        }

        const card = this.#card;

        if (card === null) {
            return [];
        }

        const rank = getRankGlyph(card.rank);
        const suit = getSuitGlyph(card.suit);
        const wild = isWild(card);

        return [
            h("span", { class: "index top" }, h("b", {}, rank), h("i", {}, suit)),
            h("span", { class: "pip" }, card.rank === "joker" ? rank : suit),
            h("span", { class: "index bottom" }, h("b", {}, rank), h("i", {}, suit)),
            ...(wild ? [h("span", { class: "wild-tag" }, "WILD"), h("span", { class: "wild-dot" }, "★")] : [])
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "playing-card": PlayingCard;
    }
}

customElements.define("playing-card", PlayingCard);
